import React, { useState, useEffect } from 'react';
import { useHelp } from '../context/HelpContext';
import { helpTopics, getTopicById } from '../data/helpContent';
import HelpTour from './HelpTour';
import HelpDirectory from './HelpDirectory';
import HelpTopicView from './HelpTopicView';
import styles from '../styles/helpSystem.module.css';

const HelpModal: React.FC = () => {
  const { isHelpOpen, closeHelp, initialTopicId, initialMode } = useHelp();
  const [mode, setMode] = useState<'tour' | 'browse' | 'topic'>(initialMode);
  const [currentTopicId, setCurrentTopicId] = useState<string>(initialTopicId || helpTopics[0].id);

  useEffect(() => {
    if (isHelpOpen) {
      setMode(initialMode);
      setCurrentTopicId(initialTopicId || helpTopics[0].id);
    }
  }, [isHelpOpen, initialTopicId, initialMode]);

  if (!isHelpOpen) return null;

  const currentTopic = getTopicById(currentTopicId);
  if (!currentTopic) return null;

  const handleTopicSelect = (topicId: string) => {
    setCurrentTopicId(topicId);
    setMode('topic');
  };

  const handleTourNavigate = (topicId: string) => {
    setCurrentTopicId(topicId);
    // Stay in tour mode when navigating
  };

  const handleStartTour = () => {
    setCurrentTopicId(helpTopics[0].id);
    setMode('tour');
  };

  const handleSwitchToBrowse = () => {
    setMode('browse');
  };

  const handleSwitchToTour = () => {
    setMode('tour');
  };

  return (
    <div className={styles.modalOverlay} onClick={closeHelp}>
      <div className={styles.modalContent} onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className={styles.modalHeader}>
          <div className={styles.headerLeft}>
            <h2 className={styles.modalTitle}>
              🎓 Help & Tour
            </h2>
          </div>
          <div className={styles.headerRight}>
            <div className={styles.modeToggle}>
              <button
                className={`${styles.modeButton} ${mode === 'tour' ? styles.activeModeButton : ''}`}
                onClick={handleSwitchToTour}
                title="Guided tour through all features"
              >
                Tour
              </button>
              <button
                className={`${styles.modeButton} ${mode === 'browse' || mode === 'topic' ? styles.activeModeButton : ''}`}
                onClick={handleSwitchToBrowse}
                title="Browse topics by category"
              >
                Browse
              </button>
            </div>
            <button className={styles.closeButton} onClick={closeHelp} title="Close help">
              ×
            </button>
          </div>
        </div>

        {/* Content */}
        <div className={styles.modalBody}>
          {mode === 'tour' && (
            <HelpTour
              currentTopic={currentTopic}
              onNavigate={handleTourNavigate}
              onSwitchToBrowse={handleSwitchToBrowse}
            />
          )}

          {mode === 'browse' && (
            <HelpDirectory
              onTopicSelect={handleTopicSelect}
              onStartTour={handleStartTour}
            />
          )}

          {mode === 'topic' && (
            <div className={styles.topicMode}>
              <HelpTopicView
                topic={currentTopic}
                showNavigation={false}
                onTopicClick={handleTopicSelect}
              />
              <div className={styles.topicModeFooter}>
                <button
                  className={styles.backToBrowseButton}
                  onClick={handleSwitchToBrowse}
                >
                  ← Back to Directory
                </button>
                <button
                  className={styles.startTourButton}
                  onClick={handleStartTour}
                >
                  🎓 Start Tour from Beginning
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default HelpModal;

